import { graphql } from 'app/generated/gql'
import { useQuery } from 'urql'
import { excludeHeld } from 'app/lib/heldMessages'
import { debug } from 'lib/logging'

import type { ContactJson } from 'app/types/entities'
import { contactLastComm, type CommDirection, type LastComm } from 'lib/lastCommMedium'

type CommContact = Pick<ContactJson, `id` | `lastCommDate` | `lastCommActivityType` | `lastCommActivitySubType`>
export interface CommContext {
  lastComm?: LastComm
  /** True when any inbound message is still unread by staff. */
  hasUnread: boolean
}
export type CommContextMap = Record<string, CommContext>

interface MessageHit {
  contactId?: string
  channel?: string
  sentByUserId?: string
  isAgent?: boolean
  createdDate?: string
  read?: boolean
}

export const PIPELINE_COMM_QUERY = graphql(`
  query PipelineCommContext($search: String, $pagination: PaginationInput) {
    messages(search: $search, pagination: $pagination) {
      hits {
        contactId
        channel
        sentByUserId
        isAgent
        createdDate
        read
      }
    }
  }
`)

// Hoisted so it isn't a string literal inside the useQuery(...) call (the no-inline-graphql
// ast-grep rule flags any template literal there). Show cached comms, then refetch.
const COMM_REQUEST_POLICY = `cache-and-network` as const

export function messageDirection(m: Pick<MessageHit, `sentByUserId` | `isAgent`>): CommDirection {
  return !m.sentByUserId && !m.isAgent ? `inbound` : `outbound`
}

export function deriveCommContext(messages: MessageHit[], contacts: CommContact[] = []): CommContextMap {
  const map: CommContextMap = {}
  for (const m of messages) {
    if (!m.contactId) continue
    const entry = (map[m.contactId] ??= { hasUnread: false })
    if (messageDirection(m) === `inbound` && m.read !== true) entry.hasUnread = true
  }
  for (const contact of contacts) {
    if (!contact.id) continue
    const entry = (map[contact.id] ??= { hasUnread: false })
    entry.lastComm = contactLastComm(contact)
  }
  return map
}

/**
 * Messages determine unread status only. Last communication comes from the contact
 * descriptor so calls and archived texts agree with the contacts table.
 */
export function usePipelineCommContext(contacts: CommContact[], enabled: boolean): CommContextMap {
  const scoped = enabled ? contacts : []
  const ids = scoped.map((contact) => contact.id).filter(Boolean)
  const search = ids.length ? excludeHeld(`contactId:(${ids.join(` OR `)})`) : undefined
  const [result] = useQuery({
    query: PIPELINE_COMM_QUERY,
    variables: { search, pagination: { limit: 2000 } },
    pause: ids.length === 0,
    requestPolicy: COMM_REQUEST_POLICY,
  })
  const hits: MessageHit[] = (result.data?.messages?.hits ?? []).map((message) => ({
    ...message,
    contactId: message.contactId ?? undefined,
    sentByUserId: message.sentByUserId ?? undefined,
    isAgent: message.isAgent ?? undefined,
    read: message.read ?? undefined,
  }))
  if (hits.length >= 2000) {
    debug([`pipelines`], `comm context hit the 2000-message page limit; some contacts' unread badges may be missing`)
  }
  return enabled ? deriveCommContext(hits, scoped) : {}
}
